import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Row,
  Space,
  Tag,
  Typography,
  Input,
  Spin,
  message,
  Modal,
  notification,
} from 'antd';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../../api/useAPIMutation';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import Form from '../../components/Form';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import { CandidateTagCollection } from '../../types';

interface CandidateTagsFormValues {
  name: string;
  candidateTags: CandidateTagCollection[];
}

const CandidateTags = () => {
  const { t } = useTranslation();

  const candidateTagsPages = useInfiniteAPIQuery('/candidate-tags/');
  const candidateTags = usePrefetchPages(candidateTagsPages, (data) => data.results);

  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const addMutation = useAPIMutation(
    async ({ name }: { name: string }, api) => {
      await api.post('/candidate-tags/', { name });
    },
    {
      onSuccess: async () => {
        form.setFieldValue('name', '');
        message.success(t('Tag adicionada com sucesso'));
        queryClient.invalidateQueries(['/candidate-tags/']);
      },
      onError: (err, variables, context) => {
        if (
          err.response?.status === 400 &&
          err.response?.data?.name &&
          err.response.data.name.length > 0 &&
          // TODO: refatorar aqui. Armengue temporário para não ficar quebrando em sandbox, mas precisamos refatorar p usar código de erro
          (err.response.data.name[0] === 'candidate tag com este name já existe.' ||
            err.response.data.name[0] === 'candidate tag with this name already exists.')
        ) {
          return notification.error({ message: t('Uma tag com esse nome já foi cadastrada.') });
        }

        return queryClient.defaultMutationOptions().onError?.(err, variables, context);
      },
    }
  );

  const removeMutation = useAPIMutation(
    async ({ id }: { id: number }, api) => {
      await api.delete(`/candidate-tags/${id}/`);
    },
    {
      onSuccess: () => {
        message.success(t('Tag removida com sucesso'));
        queryClient.invalidateQueries(['/candidate-tags/']);
      },
    }
  );

  const nameValue = Form.useWatch('name', form);

  const addTag = (name: string) => {
    if (!nameValue) {
      return;
    }

    addMutation.mutate({ name });
  };

  const showConfirm = (id: number) => {
    Modal.confirm({
      title: t('Confirmar exclusão'),
      content: (
        <Typography.Text>
          <Trans>Ao apagar essa tag ela será excluída das opções de cadastro e também de</Trans>{' '}
          <Typography.Text strong>
            <Trans>todos os candidatos</Trans>
          </Typography.Text>
          .
        </Typography.Text>
      ),
      okText: t('Apagar'),
      onOk: () => removeMutation.mutate({ id }),
      okButtonProps: {
        loading: removeMutation.isLoading,
        danger: true,
      },
      cancelText: t('Cancelar'),
      onCancel: () => {
        form.resetFields();
      },
    });
  };

  return (
    <Spin spinning={candidateTagsPages.isLoading}>
      <Helmet>
        <title>
          {t('Tags dos Candidatos | Configurações')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Form<CandidateTagsFormValues>
        form={form}
        layout="vertical"
        initialValues={{
          name: '',
          candidateTags: candidateTags || [],
        }}
      >
        <Form.Legend>
          <Trans>Configuração de tags nos candidatos:</Trans>
        </Form.Legend>
        <Space direction="vertical" size="middle">
          <Typography.Text>
            <Trans>
              Configure as tags que poderão ser inseridas no perfil dos candidatos para separá-los
              por categorias ou características específicas
            </Trans>
          </Typography.Text>
          <Row />
          <Space.Compact>
            <Form.Item name="name" noStyle>
              <Input
                placeholder={t('Adicionar tag')}
                onPressEnter={() => addTag(nameValue)}
                disabled={addMutation.isLoading}
              />
            </Form.Item>
            <Button onClick={() => addTag(nameValue)}>
              {addMutation.isLoading ? <LoadingOutlined spin /> : <PlusOutlined />}
            </Button>
          </Space.Compact>
          <Form.List name="candidateTags">
            {(fields) => (
              <Space direction="vertical" size="middle">
                <Form.Row>
                  <Form.Col>
                    {fields.map(({ key, name }) => (
                      <Tag
                        key={key}
                        closable
                        onClose={(e) => {
                          e.preventDefault();
                          showConfirm(form.getFieldValue(['candidateTags', name]).id);
                        }}
                      >
                        {form.getFieldValue(['candidateTags', name]).name}
                      </Tag>
                    ))}
                  </Form.Col>
                </Form.Row>
              </Space>
            )}
          </Form.List>
        </Space>
      </Form>
    </Spin>
  );
};

export default CandidateTags;
